
import Vue, { onBeforeMount, defineComponent, ref } from 'vue';
import { prefStorage } from '@samknows/utils';
import { BaseStateIndicator } from '@samknows/design-system';
import { useRouter } from 'vue-router/composables';

import { setupUser, removeUser } from '@/common/services/account/user';
import { ASYNC_STATE } from '@/common/global.interfaces';
import {
  getDefaultPath,
  goToThousandEyesLogin
} from '@/common/services/account/userNavigation';
import { Accessibles, Grants } from '@/common/services/account/types';
import LogoutButton from '@/common/components/LogoutButton.vue';
import { useWhiteLabelling } from '@/common/composables/whitelabel';
import { setSavePreferencesOnServerHook } from '@/common/services/account/userPreferences';
import { isThousandEyesUserLoggedIn } from '@/common/services/account/authentication';
import { InvalidUser } from '@/common/services/account/userSession';
import { isNavigationFailure, NavigationFailureType } from 'vue-router';

/**
 * The role of this page is to check if the user:
 * - Has a valid session on SamKnows side
 * - Setup the user for future user on private pages
 * - Setup branding linked to the user
 * - Redirect user to ThousandEyes login if they are not authenticated
 * - Show feedback to the user while we setup the user and in case of errors
 * For a full diagram flow, check: https://thousandeyes.atlassian.net/wiki/spaces/~71202066b1e8c7f4684ef3a36785c67621df9a/pages/3545137917/ThousandEyes+Authentication+SamKnows+user+flow
 *
 */
export default defineComponent({
  components: {
    BaseStateIndicator,
    LogoutButton
  },
  setup() {
    const router = useRouter();
    const state = ref(ASYNC_STATE.LOADING);
    const { setupBranding } = useWhiteLabelling();

    onBeforeMount(async () => {
      try {
        await setupUser();

        const accessibles: Accessibles = prefStorage.get('accessibles');
        const grants: Grants = prefStorage.get('grants');
        const hasNoAccessibles =
          !accessibles || Object.keys(accessibles).length === 0;
        const hasNoGrants = !grants || Object.keys(grants).length === 0;

        if (hasNoAccessibles || hasNoGrants) {
          state.value = ASYNC_STATE.ERROR;

          return;
        }

        setSavePreferencesOnServerHook();
        await setupBranding();

        const defaultPath = getDefaultPath();
        // Do a full page reload instead of using router so the locale is loaded if the user preferences are different to the current locale
        if (
          prefStorage.get('locale') &&
          Vue.config.lang !== prefStorage.get('locale')
        ) {
          window.location.href = defaultPath;
        }
        try {
          await router.push(defaultPath);
        } catch (error) {
          // We expect a navigation failure if the user is redirected to the TOS acceptance page
          if (!isNavigationFailure(error, NavigationFailureType.redirected)) {
            console.error(error);
          }
        }
      } catch (error) {
        if (error instanceof InvalidUser) {
          removeUser();

          const isAppProxied = process.env.NODE_ENV !== 'production';

          // By default, logged in non-consumer users in ThousandEyes are redirected to /. Root proxying is not enabled by default in webpack and results in issues.
          // To prevent a loop locally, we redirect to /connected-devices manually
          if (isAppProxied) {
            window.location.href = '/connected-devices';
            return;
          }

          goToThousandEyesLogin();
          return;
        }

        // If user setup failed, check user is logged in on ThousandEyes side
        const isAuthenticated = await isThousandEyesUserLoggedIn();

        // Clean up and prompt user to login if check failed
        if (!isAuthenticated) {
          removeUser();
          goToThousandEyesLogin();
          return;
        }

        // Show error as fallback
        console.error(error);
        state.value = ASYNC_STATE.ERROR;
      }
    });

    return {
      state
    };
  }
});
